import { ArticoloType, EditorColoreApiType, TecnicaType } from '@utils/types'
import {
  applyAlphaToHexColor,
  convertHexToPantone,
  normalizeHexColor,
  splitHexColorAndAlpha,
} from './index'

/**
 * API colore condivise tra editor e altri componenti.
 * Le logiche colore specifiche di `useFabricEditor` vivono in `fabric-editor-colors.ts`.
 */

export type EditorResolvedColorLimits = {
  hasColorLimits: boolean
  allowedColors: EditorColoreApiType[]
  lockColorChange: EditorColoreApiType | null
  isGlobalColorMode: boolean
}

export const resolveEditorColorLimits = (
  tecnica?: TecnicaType | null,
  articoloPrincipale?: ArticoloType | null
): EditorResolvedColorLimits => {
  const isColorLimitedMode = !!tecnica?.editor_modalita_colori_fissi
  const tecnicaPantoniConsentiti = Array.isArray(tecnica?.editor_pantoni_consentiti)
    ? tecnica.editor_pantoni_consentiti
    : []

  if (
    !isColorLimitedMode &&
    tecnicaPantoniConsentiti.length === 0 &&
    !tecnica?.editor_forza_colore
  ) {
    return {
      hasColorLimits: false,
      allowedColors: [],
      lockColorChange: null,
      isGlobalColorMode: false,
    }
  }

  const allowedFromTecnicaPantoni = Array.from(
    new Set(
      tecnicaPantoniConsentiti
        .map((pantone: any) => normalizeHexColor(pantone?.hex || ''))
        .filter(Boolean)
    )
  )

  if (allowedFromTecnicaPantoni.length > 0) {
    return {
      hasColorLimits: true,
      allowedColors: tecnicaPantoniConsentiti,
      lockColorChange: tecnicaPantoniConsentiti?.length === 1 ? tecnicaPantoniConsentiti[0] : null,
      isGlobalColorMode: isColorLimitedMode,
    }
  }

  if (!!tecnica?.editor_forza_colore) {
    return {
      hasColorLimits: true,
      allowedColors: [tecnica?.editor_forza_colore],
      lockColorChange: tecnica?.editor_forza_colore,
      isGlobalColorMode: false,
    }
  }

  const articoloColoreByTecnica = articoloPrincipale?.editor_colori?.find(
    (c) => c.tecnica_id === tecnica.id
  )?.editor_colore

  const primaryEditorColor =
    articoloColoreByTecnica || articoloPrincipale?.editor_colore || tecnica?.editor_colore
  const fallbackEditorColor =
    tecnica?.editor_colore || articoloColoreByTecnica || articoloPrincipale?.editor_colore
  const allowedColors = [primaryEditorColor || fallbackEditorColor].filter(Boolean)

  return {
    hasColorLimits: allowedColors.length > 0,
    allowedColors,
    lockColorChange: allowedColors.length === 1 ? allowedColors[0] : null,
    isGlobalColorMode: false,
  }
}

/**
 * Converte un HEX nell'HEX del Pantone più vicino.
 * Se la conversione fallisce restituisce il colore originale normalizzato.
 */
export const getPantoneHex = (hex: string, pantoni: EditorColoreApiType[] = []) => {
  try {
    const sourceColor = splitHexColorAndAlpha(hex)
    if (!sourceColor) return normalizeHexColor(hex)

    const pantone = convertHexToPantone(hex, pantoni)
    if (!pantone?.hex) return hex

    return applyAlphaToHexColor(pantone.hex, sourceColor.alphaHex)
  } catch (error) {
    console.warn(`Could not convert color ${hex} to Pantone`, error)
    return normalizeHexColor(hex)
  }
}

export const toAlphaHex = (alpha?: number) => {
  if (!Number.isFinite(alpha)) return null
  const normalized = Math.max(0, Math.min(255, Math.round((alpha as number) * 255)))
  return normalized.toString(16).padStart(2, '0')
}
